import { useCallback, useState } from 'react';
import styled from 'styled-components';
import {
  EmptyState,
  EmptyStateTitle,
  EmptyStateText,
} from '@spotify-internal/encore-web';
import { ApprovalStatus } from '@hooks/useAlbumInfo/types';
import { useGetProfileProtectionData } from '@hooks/useGetProfileProtectionData';
import { usePagination } from '@hooks/usePagination';
import { useProfileProtectionsCopy } from '@hooks/useProfileProtectionsCopy';
import { useProfileProtectionExtendedBeta } from '@hooks/useProfileProtectionExtendedBeta';
import { Loading } from '@components/shared/Loading';
import { LoadingError } from '@components/shared/LoadingError';
import { ProfileProtectionsTable } from './ProfileProtectionsTable';
import { ApprovalStatusFilter } from './ApprovalStatusFilter';

const StyledEmptyState = styled(EmptyState)`
  min-height: 400px;
`;

const PaginationContainer = styled.div`
  position: relative;
`;

export const ProfileProtections = () => {
  const { page, limit, renderPagination, setPage } = usePagination();
  const copy = useProfileProtectionsCopy();
  const { isExtendedBetaEnabled } = useProfileProtectionExtendedBeta();
  const [selectedStatuses, setSelectedStatuses] = useState<ApprovalStatus[]>(
    [],
  );

  const { data, paginationContext, isLoading, error } =
    useGetProfileProtectionData(selectedStatuses, limit, page);

  const handleFilterApply = useCallback(
    (statuses: ApprovalStatus[]) => {
      setSelectedStatuses(statuses);
      setPage(1);
    },
    [setPage],
  );

  if (isLoading) {
    return <Loading />;
  }

  if (error) {
    return <LoadingError />;
  }

  const hasActiveFilters = selectedStatuses.length > 0;

  const getEmptyStateCopy = () => {
    if (hasActiveFilters) {
      return { title: copy.noReleasesFound, text: copy.noReleasesFoundText };
    }
    if (isExtendedBetaEnabled) {
      return { title: copy.noReleasesToShow, text: copy.noReleasesToShowText };
    }
    return {
      title: copy.noReleasesNeedApproval,
      text: copy.noReleasesNeedApprovalText,
    };
  };

  const { title: emptyTitle, text: emptyText } = getEmptyStateCopy();

  return (
    <>
      <ApprovalStatusFilter
        selectedStatuses={selectedStatuses}
        onApply={handleFilterApply}
      />
      {data.length === 0 ? (
        <StyledEmptyState variant="contextual" maxInlineSize="700px">
          <EmptyStateTitle>{emptyTitle}</EmptyStateTitle>
          <EmptyStateText>{emptyText}</EmptyStateText>
        </StyledEmptyState>
      ) : (
        <>
          <ProfileProtectionsTable rows={data} />
          <PaginationContainer>
            {renderPagination({
              totalResultRows: data.length,
              isLastPage: paginationContext?.lastPage,
              customRowsPerPageText: copy.resultsPerPage,
            })}
          </PaginationContainer>
        </>
      )}
    </>
  );
};
